import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { NavigationRoute } from '@app/models';
import { AuthService } from '@auth0/auth0-angular';
import { BehaviorSubject, filter, map, merge, Subject } from 'rxjs';
import { LoginService } from './login.service';

@Injectable()
export class NavService {
  protected route: ActivatedRoute = inject(ActivatedRoute);
  protected router: Router = inject(Router);
  protected auth: AuthService = inject(AuthService);
  protected login: LoginService = inject(LoginService);

  private _$subnavRoutes: BehaviorSubject<NavigationRoute[]> = new BehaviorSubject<NavigationRoute[]>([]);
  private _$navChanged: Subject<void> = new Subject<void>();

  get $subnavRoutes() {
    return this._$subnavRoutes.asObservable();
  }

  public site = 'ca';

  constructor() {
    this.setSubnavRoutes();
    this.getSubnavRoutes();
  }

  public getSubnavRoutes(): void {
    merge(this.auth.isAuthenticated$, this.router.events.pipe(filter(event => event instanceof NavigationEnd)))
      .pipe(
        map(() => {
          let data: any = {};
          let route = this.route;
          while (route) {
            data = { ...data, ...route.snapshot.data };
            if (route.firstChild) {
              route = route.firstChild;
            } else {
              return data;
            }
          }
          return data;
        })
      )
      .subscribe(data => {
        const urlSection = this.route.snapshot.firstChild?.url?.toString();
        const navRoutes: NavigationRoute[] = this.getDefaultSubnavRoutes(
          urlSection,
          data.isPrequal ? 'prequalification' : 'cupccaa'
        );
        this.setSubnavRoutes(navRoutes);
      });
  }

  setSubnavRoutes(routes?: NavigationRoute[]) {
    if (!routes) {
      routes = [];
    }
    this._$subnavRoutes.next(routes);
  }

  clearSubnavRoutes() {
    this._$subnavRoutes.next([]);
  }

  private managementRoutes(usersRoute: string, rolesRoute: string): NavigationRoute[] {
    const routes: NavigationRoute[] = [];
    if (this.login.canAccessFeature('pUserAdmin')) routes.push({ route: usersRoute, name: 'User Management' });
    if (this.login.canAccessFeature('pRoleAdmin')) routes.push({ route: rolesRoute, name: 'Role Management' });
    return routes;
  }

  getDefaultSubnavRoutes(urlSection = '', questionnaireType = 'cupccaa') {
    const url = this.router.url;
    if (url.startsWith('/agency/management') || url.startsWith('/agency/roles')) {
      return this.managementRoutes('/agency/management', '/agency/roles');
    }
    if (url.startsWith('/qbat/users') || url.startsWith('/qbat/roles')) {
      return this.managementRoutes('/qbat/users', '/qbat/roles');
    }
    if (urlSection == 'qbat') {
      return [
        {
          route: '/qbat/' + questionnaireType + '/fifo',
          name: 'FIFO',
        },
        {
          route: '/qbat/' + questionnaireType + '/agencies',
          name: 'Agencies',
        },
        {
          route: '/qbat/' + questionnaireType + '/contractors',
          name: 'Contractors',
        },
      ];
    } else if (urlSection == 'vendor') {
      return [
        {
          route: '/vendor/applications/prequalification',
          name: 'Prequalification',
        },
        {
          route: '/vendor/applications/cupccaa',
          name: 'CUPCCAA',
        },
      ];
    } else if (urlSection == 'agency') {
      return [
        {
          route: '/agency/applications/prequalification',
          name: 'Prequalification',
        },
        {
          route: '/agency/applications/cupccaa',
          name: 'CUPCCAA',
        },
      ];
    }
    return [];
  }

  watchNavChanged(): Subject<void> {
    return this._$navChanged;
  }

  onNavChanged() {
    this._$navChanged.next();
  }
}
